import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { useInvestorContext } from '../../../../contexts/investor-guard'
import { copy } from '../../copy/investor-copy'
import {
  useFounderInvitesQuery,
  useRemoveFounderInviteMutation,
} from '../../hooks/use-investor-assistant'
import { formatShortDate } from '../following/dates'
import { canRemoveInvite, type FounderInviteRow, sectionInvites } from './invite-rows'
import { InviteWindow } from './invite-window'

/** The status line under a startup's name; a row whose follow started is not listed, so DECK_UP has no line. */
function statusLine(invite: FounderInviteRow): string {
  if (invite.status === 'WAITING_FOR_FOUNDER') {
    return copy('invites.status.waiting', { date: formatShortDate(invite.createdAt) })
  }
  if (invite.status === 'JOINED_WAITING_FOR_DECK') return copy('invites.status.joined')
  if (invite.status === 'REMOVED_BY_FOUNDER') return copy('invites.status.removed')
  return ''
}

const openKey = (investorId: string) => `investor-invites-open:${investorId}`

/** The open or closed choice is remembered per investor in this browser; a blocked store means open. */
function readOpen(investorId: string): boolean {
  try {
    return localStorage.getItem(openKey(investorId)) !== 'closed'
  } catch {
    return true
  }
}

function writeOpen(investorId: string, open: boolean) {
  try {
    localStorage.setItem(openKey(investorId), open ? 'open' : 'closed')
  } catch {
    // Storage is unavailable: the choice lasts for this visit only.
  }
}

/** Invites below the watchlist: the startups the investor asked a founder to join and has not yet followed. */
export function InvitesSection() {
  const { investor } = useInvestorContext()
  const invites = useFounderInvitesQuery()
  const [open, setOpen] = useState(() => readOpen(investor.id))
  const [active, setActive] = useState<{ id: string; startup: string; link: string } | null>(null)
  const remove = useRemoveFounderInviteMutation()
  const rows = sectionInvites(invites.data ?? [])
  if (rows.length === 0) return null

  const toggle = () => {
    setOpen(!open)
    writeOpen(investor.id, !open)
  }

  return (
    <section aria-labelledby="invites-heading" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2
          id="invites-heading"
          className="flex items-center gap-2 text-sm font-semibold text-foreground"
        >
          {copy('invites.heading')}
          <span className="text-xs font-normal tabular-nums text-muted-foreground">
            {rows.length}
          </span>
        </h2>
        <button
          type="button"
          aria-expanded={open}
          onClick={toggle}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {copy(open ? 'invites.hide' : 'invites.show')}
          <ChevronDown className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <ul className="divide-y divide-border rounded-xl border border-border bg-card/30">
          {rows.map((invite) => {
            return (
              <li
                key={invite.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{invite.startupName}</p>
                  <p className="text-xs text-muted-foreground">{statusLine(invite)}</p>
                </div>
                {canRemoveInvite(invite.status) && (
                  <div className="flex items-center gap-2">
                    {invite.link && (
                      <SecondaryButton
                        type="button"
                        size="sm"
                        onClick={() =>
                          setActive({
                            id: invite.id,
                            startup: invite.startupName,
                            link: invite.link ?? '',
                          })
                        }
                      >
                        {copy('invites.open')}
                      </SecondaryButton>
                    )}
                    <SecondaryButton
                      type="button"
                      size="sm"
                      tone="danger"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate({ path: { inviteId: invite.id } })}
                    >
                      {copy('invites.remove')}
                    </SecondaryButton>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {remove.isError && (
        <p role="alert" className="text-sm text-muted-foreground">
          {copy('invite.removeError')}
        </p>
      )}
      {active && (
        <InviteWindow
          key={active.id}
          open
          startup={active.startup}
          existing={active}
          onClose={() => setActive(null)}
        />
      )}
    </section>
  )
}
